import React from 'react';
import { Eye, Download, X } from 'lucide-react';
import { ProformaInvoice, COMPANY_DATA } from '../../types/proformaInvoice';
import { generateProformaInvoicePDF } from '../../utils/generateProformaInvoicePDF';

interface ProformaInvoicePreviewProps {
  invoice: ProformaInvoice;
  onClose: () => void;
  onConfirm?: () => void;
}

export const ProformaInvoicePreview: React.FC<ProformaInvoicePreviewProps> = ({
  invoice,
  onClose,
  onConfirm
}) => {
  const handleDownload = () => {
    generateProformaInvoicePDF(invoice);
    if (onConfirm) {
      onConfirm();
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-zinc-800 rounded-lg w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Nagłówek */}
        <div className="flex items-center justify-between p-4 border-b border-zinc-700">
          <div className="flex items-center gap-3">
            <Eye className="w-5 h-5 text-orange-500" />
            <h2 className="text-lg font-semibold">Podgląd faktury proforma</h2>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-zinc-400 hover:text-white transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Treść faktury */}
        <div className="flex-1 overflow-auto p-6">
          <div className="bg-white text-black rounded-lg p-8 space-y-6">
            {/* Nagłówek faktury */}
            <div className="text-center">
              <h1 className="text-2xl font-bold">FAKTURA PROFORMA</h1>
              <p className="text-lg mt-2">Nr {invoice.invoiceNumber}</p>
            </div>

            {/* Dane sprzedawcy i nabywcy */}
            <div className="grid grid-cols-2 gap-8">
              <div>
                <h3 className="font-bold mb-2">Sprzedawca:</h3>
                <p className="font-semibold">{COMPANY_DATA.name}</p>
                <p>{COMPANY_DATA.address}</p>
                <p>{COMPANY_DATA.postalCode} {COMPANY_DATA.city}</p>
                <p>NIP: {COMPANY_DATA.nip}</p>
                <p>REGON: {COMPANY_DATA.regon}</p>
              </div>

              <div>
                <h3 className="font-bold mb-2">Nabywca:</h3>
                <p className="font-semibold">{invoice.buyer.name}</p>
                <p>{invoice.buyer.address}</p>
                <p>{invoice.buyer.postalCode} {invoice.buyer.city}</p>
                {invoice.buyer.nip && <p>NIP: {invoice.buyer.nip}</p>}
                {invoice.buyer.email && <p>Email: {invoice.buyer.email}</p>}
                {invoice.buyer.phone && <p>Tel: {invoice.buyer.phone}</p>}
              </div>
            </div>

            {/* Daty */}
            <div className="space-y-1">
              <p><span className="font-semibold">Data wystawienia:</span> {new Date(invoice.issueDate).toLocaleDateString('pl-PL')}</p>
              <p><span className="font-semibold">Termin płatności:</span> {new Date(invoice.paymentDeadline).toLocaleDateString('pl-PL')}</p>
              <p><span className="font-semibold">Ważność oferty:</span> {new Date(invoice.validUntil).toLocaleDateString('pl-PL')}</p>
            </div>

            {/* Tabela z pozycjami */}
            <div className="overflow-x-auto">
              <table className="w-full border-collapse">
                <thead>
                  <tr className="bg-gray-100">
                    <th className="border border-gray-300 px-2 py-1 text-left">Lp.</th>
                    <th className="border border-gray-300 px-2 py-1 text-left">Nazwa</th>
                    <th className="border border-gray-300 px-2 py-1 text-right">Ilość</th>
                    <th className="border border-gray-300 px-2 py-1 text-left">Jedn.</th>
                    <th className="border border-gray-300 px-2 py-1 text-right">Cena netto</th>
                    <th className="border border-gray-300 px-2 py-1 text-right">VAT</th>
                    <th className="border border-gray-300 px-2 py-1 text-right">Wartość netto</th>
                    <th className="border border-gray-300 px-2 py-1 text-right">Wartość VAT</th>
                    <th className="border border-gray-300 px-2 py-1 text-right">Wartość brutto</th>
                  </tr>
                </thead>
                <tbody>
                  {invoice.items.map((item, index) => (
                    <tr key={index}>
                      <td className="border border-gray-300 px-2 py-1">{index + 1}</td>
                      <td className="border border-gray-300 px-2 py-1">{item.name}</td>
                      <td className="border border-gray-300 px-2 py-1 text-right">{item.quantity}</td>
                      <td className="border border-gray-300 px-2 py-1">{item.unit}</td>
                      <td className="border border-gray-300 px-2 py-1 text-right">{item.netPrice.toFixed(2)} zł</td>
                      <td className="border border-gray-300 px-2 py-1 text-right">
                        {item.vatRate === -1 ? 'zw.' : `${item.vatRate}%`}
                      </td>
                      <td className="border border-gray-300 px-2 py-1 text-right">{item.netValue.toFixed(2)} zł</td>
                      <td className="border border-gray-300 px-2 py-1 text-right">{item.vatValue.toFixed(2)} zł</td>
                      <td className="border border-gray-300 px-2 py-1 text-right font-semibold">
                        {item.grossValue.toFixed(2)} zł
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="font-semibold">
                    <td colSpan={6} className="border border-gray-300 px-2 py-1 text-right">
                      Razem:
                    </td>
                    <td className="border border-gray-300 px-2 py-1 text-right">
                      {invoice.summary.netTotal.toFixed(2)} zł
                    </td>
                    <td className="border border-gray-300 px-2 py-1 text-right">
                      {invoice.summary.vatTotal.toFixed(2)} zł
                    </td>
                    <td className="border border-gray-300 px-2 py-1 text-right bg-gray-100">
                      {invoice.summary.grossTotal.toFixed(2)} zł
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>

            {/* Słownie */}
            <div className="space-y-2">
              <p><span className="font-semibold">Słownie:</span> {invoice.summary.grossTotalInWords}</p>
              <p><span className="font-semibold">Sposób płatności:</span> {invoice.paymentMethod === 'transfer' ? 'przelew' : 'gotówka'}</p>
            </div>

            {/* Dane bankowe */}
            {invoice.paymentMethod === 'transfer' && (
              <div className="bg-gray-50 p-4 rounded">
                <h4 className="font-semibold mb-2">Dane do przelewu:</h4>
                <p>{COMPANY_DATA.bank.name}</p>
                <p>Nr konta: {COMPANY_DATA.bank.accountNumber}</p>
                <p>SWIFT: {COMPANY_DATA.bank.swift}</p>
              </div>
            )}

            {/* Uwagi */}
            {invoice.notes && (
              <div>
                <h4 className="font-semibold mb-1">Uwagi:</h4>
                <p className="whitespace-pre-wrap">{invoice.notes}</p>
              </div>
            )}

            {/* Stopka */}
            <div className="text-center text-sm text-gray-600 italic mt-8 pt-4 border-t">
              <p>Niniejszy dokument nie stanowi faktury VAT w rozumieniu przepisów ustawy o VAT</p>
            </div>
          </div>
        </div>

        {/* Przyciski akcji */}
        <div className="flex justify-end gap-3 p-4 border-t border-zinc-700">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-zinc-700 hover:bg-zinc-600 rounded-lg transition-colors"
          >
            Anuluj
          </button>
          <button
            onClick={handleDownload}
            className="px-4 py-2 bg-orange-600 hover:bg-orange-700 rounded-lg transition-colors flex items-center gap-2"
          >
            <Download className="w-4 h-4" />
            Pobierz PDF
          </button>
        </div>
      </div>
    </div>
  );
};
